import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import {Button} from '../../../components/ui/Button';
import { formatCurrency } from '../../../utils/localization';
import apiService from '../../../services/api';

const ExpenseSharingHistory = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedView, setSelectedView] = useState('recent');
  const [expensesData, setExpensesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const periods = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' }
  ];

  const views = [
    { value: 'recent', label: 'Recent Activity', icon: 'Clock' },
    { value: 'monthly', label: 'Monthly Summary', icon: 'Calendar' },
    { value: 'categories', label: 'Categories', icon: 'PieChart' }
  ];

  useEffect(() => {
    const fetchExpenseHistory = async () => {
      try {
        const response = await apiService.request(`/users/expenses/history?period=${selectedPeriod}`);
        if (response.success) {
          setExpensesData(response.data);
        } else {
          throw new Error(response.message || 'Failed to fetch expense history');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching expense history:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchExpenseHistory();
  }, [selectedPeriod]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      settled: { color: 'text-green-700', bg: 'bg-green-100', label: 'Settled' },
      pending: { color: 'text-yellow-700', bg: 'bg-yellow-100', label: 'Pending' },
      active: { color: 'text-blue-700', bg: 'bg-blue-100', label: 'Active' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config?.bg} ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const renderRecentActivity = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <p>Unable to load expense history</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      );
    }

    const recentExpenses = expensesData?.recentExpenses || [];
    
    return (
      <div className="space-y-4">
        {recentExpenses?.map((expense) => (
          <div key={expense?.id} className="p-4 border border-border rounded-lg hover:bg-muted/50 transition-micro">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h4 className="text-sm font-medium text-foreground">{expense?.expenseDescription}</h4>
                <p className="text-xs text-muted-foreground">{expense?.eventName}</p>
              </div>
              {getStatusBadge(expense?.status)}
            </div>
            
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Total: </span>
                  <span className="font-medium text-foreground">{formatCurrency(expense?.amount)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Your share: </span>
                  <span className="font-medium text-foreground">{formatCurrency(expense?.yourShare)}</span>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                {new Date(expense?.date)?.toLocaleDateString('en-IN')}
              </div>
            </div>
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center space-x-2">
                <span className="text-xs text-muted-foreground">Paid by:</span>
                <span className="text-xs font-medium text-foreground">{expense?.paidBy}</span>
              </div>
              <div className="flex items-center space-x-1">
                <span className="text-xs text-muted-foreground">
                  {expense?.participants?.length + 1} people
                </span>
                <div className="flex -space-x-1">
                  {expense?.participants?.slice(0, 3)?.map((participant, index) => (
                    <div
                      key={index}
                      className="w-5 h-5 bg-muted border border-background rounded-full flex items-center justify-center"
                      title={participant}
                    >
                      <span className="text-xs font-medium text-muted-foreground">
                        {participant?.charAt(0)}
                      </span>
                    </div>
                  ))}
                  {expense?.participants?.length > 3 && (
                    <div className="w-5 h-5 bg-muted border border-background rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-muted-foreground">
                        +{expense?.participants?.length - 3}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMonthlySummary = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <p>Unable to load monthly summary</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      );
    }

    const monthlyStats = expensesData?.monthlyStats || [];
    
    return (
      <div className="space-y-4">
        {monthlyStats?.map((stat, index) => (
          <div key={index} className="p-4 border-border rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-foreground">{new Date(stat?.month).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</h4>
              <Icon name="Calendar" size={16} className="text-muted-foreground" />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-2 bg-muted rounded-lg">
                <div className="text-lg font-semibold text-foreground">
                  {formatCurrency(stat?.totalExpenses)}
                </div>
                <div className="text-xs text-muted-foreground">Total Expenses</div>
              </div>
              
              <div className="text-center p-2 bg-muted rounded-lg">
                <div className="text-lg font-semibold text-foreground">
                  {formatCurrency(stat?.expenseCount)}
                </div>
                <div className="text-xs text-muted-foreground">Number of Expenses</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCategories = () => (
    <div className="space-y-4">
      <div className="text-center py-8">
        <Icon name="PieChart" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h4 className="text-lg font-medium text-foreground mb-2">Category Analysis</h4>
        <p className="text-sm text-muted-foreground mb-4">
          View your spending patterns by expense categories
        </p>
        <Button variant="outline" size="sm" iconName="BarChart" iconPosition="left">
          View Categories
        </Button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (selectedView) {
      case 'recent':
        return renderRecentActivity();
      case 'monthly':
        return renderMonthlySummary();
      case 'categories':
        return renderCategories();
      default:
        return renderRecentActivity();
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="History" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Expense Sharing History</h3>
            <p className="text-sm text-muted-foreground">
              Track your expense sharing activity and patterns
            </p>
          </div>
        </div>
      </div>
      {/* Controls */}
      <div className="flex items-center flex-col sm:flex-row justify-between mb-6 border-b border-border pb-4">
        {/* View Toggle */}
        <div className="flex flex-col w-full gap-2 sm:flex-row sm:w-auto items-center space-x-1 bg-muted rounded-lg p-1">
          {views?.map((view) => (
            <button
              key={view?.value}
              onClick={() => setSelectedView(view?.value)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-micro ${
                selectedView === view?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={view?.icon} size={14} />
              <span>{view?.label}</span>
            </button>
          ))}
        </div>

        {/* Period Filter */}
        <select
          value={selectedPeriod}
          onChange={(e) => {
            setSelectedPeriod(e?.target?.value);
          }}
          className="px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent text-sm"
        >
          {periods?.map((period) => (
            <option key={period?.value} value={period?.value}>
              {period?.label}
            </option>
          ))}
        </select>
      </div>
      {/* Content */}
      {renderContent()}
      {/* View All Button */}
      {selectedView === 'recent' && !loading && !error && (
        <div className="mt-6 pt-4 border-t border-border text-center">
          <Button variant="outline" size="sm" iconName="ArrowRight" iconPosition="right">
            View Complete History
          </Button>
        </div>
      )}
    </div>
  );
};

export default ExpenseSharingHistory;
